import { ReactComponent as IconDiscountCoupon } from "../../assets/icons/discount_ticket.svg";
import { ReactComponent as IconArrowRight } from "../../assets/icons/chevron_right.svg";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { classes, style } from "typestyle";
import { PromoStatus } from "../../reducers/availableTicketTypes";
import { unit } from "../../styles";
import colors from "../../styles/colors";
import PromoAlert from "../PromoAlert";
import Spinner from "../Spinner";

const wrapperClx = style({ marginTop: unit(4), marginBottom: unit(6) });
const containerClx = style({ position: "relative" });

const inputClx = style({
  backgroundColor: "white",
  height: unit(14),
  width: "100%",
  paddingLeft: unit(8 + 3 + 3),
  fontWeight: 600,
  borderRadius: 7,
  border: `1px solid ${colors.grey[300]}`,
  outline: "none",

  $nest: {
    "&:active, &:focus": {
      borderColor: "rgba(150, 90, 250, 1)",
    },
  },
});

const inputThing = style({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: unit(8),
  height: unit(8),
  top: unit(3),
  borderRadius: "50%",
});

const iconClx = style({
  left: unit(3),
});

const submitButtonClx = style({
  right: unit(3),
  border: 0,
  padding: 0,
  outline: "none",
  backgroundColor: "rgba(150, 90, 250, 1)",
  color: "white",
});

const tryAgainButtonClx = style({
  background: "transparent",
  border: 0,
  padding: 0,
  textDecoration: "underline",
});

type PromoInputProps = {
  onSubmit: (text: string) => void;
  loading: boolean;
  status: PromoStatus;
  resetStatus: () => void;
};

const PromoInput: React.FC<PromoInputProps> = ({ onSubmit, loading, status, resetStatus }) => {
  const [text, setText] = React.useState("");

  const intl = useIntl();

  const onSubmitClicked = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onSubmit(text);
  };

  const reset = () => {
    setText("");
    resetStatus();
  };

  return (
    <div className={wrapperClx}>
      {(status === PromoStatus.NotSet || status === PromoStatus.Verifying) && (
        <>
          <form onSubmit={onSubmitClicked} className={containerClx}>
            <div className={classes(inputThing, iconClx)}>
              <IconDiscountCoupon height={24} width={24} color={colors.grey[300]} />
            </div>
            <input
              autoFocus
              className={inputClx}
              placeholder={intl.formatMessage({
                id: "components.TicketBox.PromoInput.placeholder",
                defaultMessage: "Code",
              })}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <button
              className={classes(inputThing, submitButtonClx)}
              type="submit"
              onClick={onSubmitClicked}
              disabled={loading}
            >
              {loading ? (
                <Spinner size="small" theme="white" />
              ) : (
                <IconArrowRight height={16} width={16} color={"white"} />
              )}
            </button>
          </form>
        </>
      )}
      {status === PromoStatus.WrongfulPromo && (
        <PromoAlert
          title="Sorry"
          icon="sadNacho"
          content={
            <>
              <div>
                <FormattedMessage
                  id="components.TicketBox.promocode.notValid"
                  defaultMessage="This is not a valid promocode."
                />
              </div>
              <button className={tryAgainButtonClx} onClick={reset}>
                <FormattedMessage
                  id="components.TicketBox.promocode.tryAgain"
                  defaultMessage="Try again."
                />
              </button>
            </>
          }
        />
      )}
      {status === PromoStatus.SuccessfulPromo && (
        <PromoAlert
          title={
            <FormattedMessage
              id="components.TicketBox.promocode.success"
              defaultMessage="Success"
            />
          }
          icon="happyNacho"
          content={
            <>
              <div>
                <FormattedMessage
                  id="components.TicketBox.promocode.promoAdded"
                  defaultMessage="Promocode ticket is added to the list above."
                />
              </div>
              <button className={tryAgainButtonClx} onClick={reset}>
                <FormattedMessage
                  id="components.TicketBox.promocode.addAnother"
                  defaultMessage="Add another."
                />
              </button>
            </>
          }
        />
      )}
    </div>
  );
};

export default PromoInput;
